import { useCallback, useEffect, useState } from 'react';
import type { ComponentProps } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { doesCompanyExistInWatchlist } from '../../utils';

import useWatchlist from '../../hooks/useWatchlist';
import useWatchlists from '../../hooks/useWatchlists';
import useWatchlistMutations from '../../hooks/useWatchlistMutations';

import useUser from '@/hooks/useUser';

import AddRemoveCompanyButton from './AddRemoveCompanyButton';

const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);

const LimitModal = loadable(() => import('../../components/LimitModal'));
const AddToWatchlistsModal = loadable(
  () => import('../../components/AddToWatchlistsModal')
);

type Props = Omit<
  ComponentProps<typeof AddRemoveCompanyButton>,
  'isLoading' | 'existsInWatchlist'
> & {
  companyId: string;
  tickerSymbol: string;
  minimal?: boolean;
  disabled?: boolean;
};

type Modal =
  | 'company-limit-modal'
  | 'guest-registration-modal'
  | 'add-to-watchlists-modal';

export const AddRemoveCompanyToggle = ({
  disabled,
  companyId,
  tickerSymbol,
  minimal = false,
  styleType = 'louder',
  ...rest
}: Props) => {
  const { trackEvent } = useTracking();
  const [modal, setModal] = useState<Modal | null>(null);
  const { data: user } = useUser();
  const {
    id: watchlistId,
    itemsLimit,
    keys: watchlistKeys,
    isFetched: isWatchlistFetched,
  } = useWatchlist();
  const {
    watchlists,
    watchlistsKeys,
    isFetched: isWatchlistsFetched,
  } = useWatchlists();
  const { addCompany, addCompanyStatus, removeCompany, removeCompanyStatus } =
    useWatchlistMutations();

  const hasFetched = isWatchlistFetched && isWatchlistsFetched;
  const hasMultipleWatchlists = watchlists.length > 1;
  const isLoading =
    addCompanyStatus === 'loading' || removeCompanyStatus === 'loading';

  const existsInSomeWatchlist = doesCompanyExistInWatchlist(
    companyId,
    hasMultipleWatchlists ? watchlistsKeys : watchlistKeys
  );

  const handleLoggedOutAddClick = useCallback(() => {
    setModal('guest-registration-modal');
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: 'addToWatchlists',
      subject: 'guestRegistrationModal',
    });
  }, [trackEvent]);

  const handleRemoveCompany = useCallback(() => {
    removeCompany({ id: companyId, watchlistId });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'remove',
      action: 'click',
    });
  }, [trackEvent, removeCompany, companyId, watchlistId]);

  const handleAddCompany = useCallback(() => {
    addCompany({ id: companyId, watchlistId });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'add',
      action: 'click',
    });
  }, [trackEvent, addCompany, companyId, watchlistId]);

  useEffect(() => {
    if (user.loggedIn && modal === 'guest-registration-modal') {
      // The user must have logged in or registered via the modal
      // So we hide the modal
      setModal(null);

      // If the user has multiple watchlists, show the 'add to watchlists' modal
      // Otherwise, add the company to their watchlist
      if (hasMultipleWatchlists) {
        setModal('add-to-watchlists-modal');
      } else {
        addCompany({
          id: companyId,
          watchlistId,
        });
      }
    }
  }, [
    user.loggedIn,
    modal,
    addCompany,
    companyId,
    watchlistId,
    hasMultipleWatchlists,
  ]);

  return (
    <>
      {modal === 'guest-registration-modal' && (
        <GuestRegistrationModal
          canClose={true}
          removeWindowScroll={true}
          onClose={() => setModal(null)}
          onRegisterSuccess={() => {
            trackEvent({
              type: 'track',
              subject: 'addToWatchLists',
              modifier: 'registration',
              action: 'success',
            });
          }}
        />
      )}
      {modal === 'company-limit-modal' && (
        <LimitModal onClose={() => setModal(null)} />
      )}
      {modal === 'add-to-watchlists-modal' && (
        <AddToWatchlistsModal
          data-cy-id="add-remove-to-watchlists-modal"
          companyId={companyId}
          tickerSymbol={tickerSymbol}
          watchlists={watchlists}
          onClose={() => setModal(null)}
        />
      )}
      <AddRemoveCompanyButton
        minimal={minimal}
        isLoading={isLoading}
        styleType={styleType}
        existsInWatchlist={existsInSomeWatchlist}
        disabled={disabled || !hasFetched || isLoading}
        data-cy-id="add-remove-to-watchlist-toggle"
        onClick={() => {
          if (!user.loggedIn) {
            handleLoggedOutAddClick();
          } else if (hasMultipleWatchlists) {
            setModal('add-to-watchlists-modal');
          } else if (existsInSomeWatchlist) {
            handleRemoveCompany();
          } else if (watchlistKeys.length === itemsLimit) {
            setModal('company-limit-modal');
          } else {
            handleAddCompany();
          }
        }}
        {...rest}
      />
    </>
  );
};

export default AddRemoveCompanyToggle;
